import React, { useEffect, useCallback, useRef, useState } from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Col,
  Row
} from 'reactstrap'
import _ from 'lodash'
import DatePicker from 'react-datepicker'
import TransComponent from 'components/Trans'
import ReactSelect from 'react-select'

import { queryHandler } from 'utils/queryHandler'

const SearchForm = ({
  per_page,
  searchParams,
  onChangeHOCState,
  getListAPI,
  renderCustomButton
}) => {
  const timeoutRef = useRef(null)
  const [lastSearchParams, setLastSearchParams] = useState(searchParams)

  // Handle parameter changes without triggering search immediately
  const handleParamChange = useCallback((index, newValue) => {
    let tmp = _.cloneDeep(searchParams)
    tmp[index]['param'] = newValue
    onChangeHOCState('searchParams', tmp)
  }, [searchParams, onChangeHOCState])

  // Debounced search effect - only trigger when searchParams actually change
  useEffect(() => {
    // Check if searchParams have actually changed from the last search
    const hasChanged = !_.isEqual(searchParams, lastSearchParams)
    
    if (hasChanged) {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set new timeout to trigger search after 600ms of no changes
      timeoutRef.current = setTimeout(() => {
        let searchQuery = queryHandler(searchParams, 0, per_page)
        getListAPI(searchQuery)
        setLastSearchParams(searchParams) // Update last search params after search
      }, 200)
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [searchParams, lastSearchParams, per_page, getListAPI])

  // Reset function
  const handleReset = useCallback(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    let tmp = []
    let tmpSearchParams = _.cloneDeep(searchParams)
    tmpSearchParams.map(item => {
      tmp.push({
        ...item,
        param: ''
      })
    })
    onChangeHOCState('searchParams', tmp)
    setLastSearchParams(tmp) // Update last search params for reset
    let searchQuery = queryHandler([], 0, per_page)
    getListAPI(searchQuery)
  }, [searchParams, onChangeHOCState, per_page, getListAPI])

  return (
    <Card className="main-card mb-3">
      <CardHeader style={{ textTransform: 'capital' }}>
        <TransComponent i18nKey="general.search" />
      </CardHeader>
      <CardBody>
        <Form onSubmit={e => e.preventDefault()}>
          <Row>
            {
              searchParams.map((item, index) => {
                return (
                  <Col key={index} md={item.col || 12} xs={12}>
                    <FormGroup key={item.value}>
                      <Label>
                        {
                          item.transKey && (
                            <TransComponent i18nKey={item.transKey} />
                          )
                        }
                        {
                          !item.transKey && (
                            `${item.label}`
                          )
                        }
                      </Label>
                      {
                        item.type === 'select' && (
                          <ReactSelect
                            isClearable
                            options={item.options.map(data => ({ value: data.id, label: data.name }))}
                            value={
                              item.options && item.options.find(opt => {
                                if (item.valueType === 'boolean') {
                                  return String(opt.id) === String(item.param);
                                }
                                return opt.id === item.param;
                              })
                                ? [{ value: item.param, label: (item.options.find(opt => opt.id === item.param) || {}).name }]
                                : null
                            }
                            onChange={option => {
                              let tempVal = option ? option.value : '';
                              if (item.valueType === 'string') {
                                tempVal = option ? String(option.value) : '';
                              }
                              if (item.valueType === 'boolean') {
                                tempVal = option ? option.value === true || option.value === 'true' : false;
                              }
                              handleParamChange(index, tempVal)
                            }}
                            placeholder=""
                            styles={{ container: (base) => ({ ...base, width: '100%' }) }}
                          />
                        )
                      }
                      {
                        item.type === 'text' && (
                          <Input
                            type={'text'}
                            value={item.param}
                            onChange={e => {
                              handleParamChange(index, e.target.value)
                            }} />
                        )
                      }
                      {
                        item.type === 'date' && (
                          <DatePicker
                            className={'w-100 form-control'}
                            showTimeSelect
                            dateFormat="dd-MM-yyyy h:mm aa"
                            selected={item.param}
                            onChange={val => {
                              handleParamChange(index, val)
                            }}
                          />
                        )
                      }
                    </FormGroup>
                  </Col>
                )
              })
            }
          </Row>
          <div className="d-flex">
            {
              renderCustomButton &&
              renderCustomButton(queryHandler(searchParams, 0, per_page))
            }
            <Button
              color="danger"
              style={{ marginLeft: 'auto' }}
              onClick={handleReset}
            >
              {<TransComponent i18nKey="general.reset" />}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  )
}

export default SearchForm